//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useDeleteCreatorsCampaignContentMutation } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsQueries';
import { CreatorsCampaignsTrackingIds } from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTrackingId';
import { BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import PartialCuid from '@models/PartialCuid';
import { TrackingId } from '@models/TrackingId';
import { pluralize } from '@utils/stringUtils';

import { clearItems } from 'app/bulk-actions/bulk-actions-actions/action-creators';

interface DeleteContentModalProps {
  campaignSlugName: string;
  closeModal: () => void;
  contentIds: PartialCuid[];
  isOpen: boolean;
  trackingId?: TrackingId;
}

export const DeleteContentModal = ({
  campaignSlugName,
  closeModal: closeModalProp,
  contentIds,
  isOpen,
  trackingId,
}: DeleteContentModalProps) => {
  const dispatch = useDispatch();
  const { isPending: isDeletingContent, mutate: deleteContent } =
    useDeleteCreatorsCampaignContentMutation();

  const closeModal = () => {
    closeModalProp();
  };

  const deleteContentHandler = () => {
    deleteContent(
      {
        campaignSlugName,
        contentIds,
      },
      {
        onSettled: () => {
          closeModal();
        },
        onSuccess: () => {
          // Clear the selected items after successful deletion
          dispatch(clearItems());
        },
      },
    );
  };

  const contentCount = contentIds.length;
  const contentText = pluralize(contentCount, 'content', 'contents', false);

  return (
    <Modal
      closeModal={closeModal}
      contents={
        <ContentWrapper>
          <BodyText>
            Are you sure you want to delete{' '}
            {pluralize(
              contentCount,
              'this content',
              `these ${contentCount} contents`,
              false,
            )}
            ? This action cannot be undone.
          </BodyText>
        </ContentWrapper>
      }
      isFooterEnabled={true}
      isModalOpen={isOpen}
      primaryButtonInfo={{
        buttonContent: `Delete ${contentText}`,
        isDisabled: isDeletingContent,
        onClick: deleteContentHandler,
        trackingId:
          CreatorsCampaignsTrackingIds.DELETE_CONTENT_CONFIRMATION_MODAL_DELETE_BUTTON,
      }}
      secondaryButtonInfo={{
        buttonContent: 'Cancel',
        onClick: closeModal,
        trackingId:
          CreatorsCampaignsTrackingIds.DELETE_CONTENT_CONFIRMATION_MODAL_CANCEL_BUTTON,
      }}
      title={`Delete ${contentText}`}
      trackingId={trackingId}
      width='medium'
    />
  );
};

const ContentWrapper = styled.div`
  position: relative;
`;
